import { DataGrid } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import axiosInstance from "utils/axiosInstance";
import dayjs from "dayjs";
import { Button, Grid, IconButton, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BiRefresh } from "react-icons/bi";
import { MdOutlineSettings } from "react-icons/md";
import { CgMathPlus } from "react-icons/cg";
import { useState } from "react";
import CreateAdvisorModal from "./CreateAdvisorModal";
import GKCommonPagination from "components/CommonPagination"; // Import your pagination component

const AdvisorTable = () => {
  const navigate = useNavigate();
  const [advisorCreateModal, setAdvisorCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    paginationData: {
      skip: 0,
      page: 0,
      take: 25,
    },
  });

  const requestBody = {
    take: filters?.paginationData?.take,
    skip: filters?.paginationData?.skip,
    search: filters?.search || "",
  };

  const handleSortChange = (model) => {
    setFilters((prev) => ({
      ...prev,
      sortModel: model,
    }));
  };

  const {
    data: advisorList,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["DASHBOARD_ADVISOR_TABLE", requestBody],
    queryFn: () =>
      axiosInstance.get(`/super_admin/advisors/`, { params: requestBody }),
    staleTime: 24 * 60 * 60 * 1000, // 1 day
  });

  const { mutate: refreshAccount } = useMutation({
    mutationKey: ["REFRESH_ACCOUNT"],
    mutationFn: (id) =>
      axiosInstance.get(`/super_admin/${id}/refresh_account/`),
    onSuccess: (response) => {},
    onError: (error) => {},
  });

  const columns = [
    {
      headerName: "Company Name",
      field: "companyName",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: "Advisor Name",
      field: "name",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 200,
      valueGetter: (row) => row || "-",
    },
    {
      headerName: "Phone Number",
      field: "phone",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      type: "number",
      valueGetter: (row) => row || "-",
    },
    {
      headerName: "Total Aum",
      field: "totalAum",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      type: "number",
      valueGetter: (row) => row || 0,
    },
    {
      headerName: "Clients",
      field: "clients",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
      type: "number",
      valueGetter: (row) => row || 0,
    },
    {
      headerName: "Portfolios",
      field: "portfolios",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 100,
      type: "number",
    },
    {
      headerName: "Created on",
      field: "createdOn",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => dayjs(params?.value).format("DD MMM YYYY"),
      valueGetter: (row) => row || "N/A",
    },
    {
      headerName: "Actions",
      field: "action",
      disableColumnMenu: true,
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                refreshAccount(params.id);
              }}
            >
              <BiRefresh />
            </IconButton>
            &nbsp;&nbsp;
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/app/preference/${params.row.phone}`);
              }}
            >
              <MdOutlineSettings />
            </IconButton>
          </>
        );
      },
    },
  ];

  const CustomPagination = () => {
    return (
      <GKCommonPagination
        totalCount={advisorList?.data?.totalCount || 0}
        page={filters?.paginationData.page}
        onPageChange={(newPage) => {
          setFilters({
            ...filters,
            paginationData: {
              ...filters.paginationData,
              page: newPage,
              skip: newPage * filters?.paginationData.take,
            },
          });
        }}
        take={filters?.paginationData.take}
        handleRowChange={(event) => {
          setFilters({
            ...filters,
            paginationData: {
              ...filters.paginationData,
              take: event.target.value,
            },
          });
        }}
      />
    );
  };

  const handleSearchChange = (event) => {
    setFilters({
      ...filters,
      search: event.target.value,
      paginationData: {
        ...filters.paginationData,
        skip: 0,
        page: 0,
      },
    });
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} display={"flex"} justifyContent={"space-between"}>
        <TextField
          sx={{ minWidth: "200px" }}
          variant="outlined"
          size="small"
          placeholder="Search Advisor"
          value={filters.search}
          onChange={handleSearchChange}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setAdvisorCreateModal(true)}
        >
          <CgMathPlus size={18} /> Create Advisor
        </Button>
      </Grid>
      <Grid item xs={12}>
        <DataGrid
          autoHeight
          loading={isFetching}
          columns={columns}
          rows={advisorList?.data?.data || []}
          onRowClick={(row) => navigate(`/app/advisor/${row.id}/details`)}
          sx={{ "--DataGrid-overlayHeight": "600px" }}
          pageSize={filters.paginationData.take}
          rowCount={advisorList?.data.totalCount}
          paginationMode="server"
          // onSortModelChange={(model) => {}}
          onSortModelChange={handleSortChange}
          slots={{
            footer: CustomPagination,
          }}
        />
        {advisorCreateModal && (
          <CreateAdvisorModal
            open={advisorCreateModal}
            setOpen={setAdvisorCreateModal}
            refetch={refetch}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default AdvisorTable;
